/**
 * YouTubeField — URL input with live thumbnail / embed preview.
 */

import { useState, useEffect } from 'react';
import { Youtube, X, ExternalLink } from 'lucide-react';
import { cn } from '@/utils/cn';
import { parseYouTubeUrl } from '@/utils/youtubeUtils';

interface YouTubeFieldProps {
  value?:    string | null;
  onChange:  (url: string | null) => void;
  disabled?: boolean;
  error?:    string;
}

export default function YouTubeField({ value, onChange, disabled, error }: YouTubeFieldProps) {
  const [inputVal, setInputVal] = useState(value ?? '');
  const parsed = parseYouTubeUrl(inputVal ?? '');

  // Sync external value → local state
  useEffect(() => {
    setInputVal(value ?? '');
  }, [value]);

  const handleChange = (raw: string) => {
    setInputVal(raw);
    if (!raw) {
      onChange(null);
      return;
    }
    const result = parseYouTubeUrl(raw);
    onChange(result.valid ? raw : raw); // always pass raw; validation done in zod
  };

  const handleClear = () => {
    setInputVal('');
    onChange(null);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-surface-700">
        YouTube Video URL
      </label>

      {/* Input */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-surface-400">
          <Youtube className="h-4 w-4" />
        </div>
        <input
          type="url"
          value={inputVal}
          onChange={e => handleChange(e.target.value)}
          disabled={disabled}
          placeholder="https://www.youtube.com/watch?v=..."
          className={cn(
            'block w-full rounded-lg border bg-white text-sm text-surface-900 pl-9 pr-9 py-2.5',
            'placeholder:text-surface-400',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            'disabled:bg-surface-50 disabled:cursor-not-allowed',
            'transition-colors',
            error
              ? 'border-red-400 focus:ring-red-400'
              : parsed.valid
                ? 'border-emerald-400 focus:ring-emerald-400'
                : 'border-surface-300 hover:border-surface-400',
          )}
        />
        {inputVal && (
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-surface-400 hover:text-surface-700"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {/* Thumbnail preview */}
      {parsed.valid && parsed.thumbnailUrl && (
        <div className="rounded-xl overflow-hidden border border-surface-200 bg-surface-50">
          <div className="relative">
            <img
              src={parsed.thumbnailUrl}
              alt="YouTube thumbnail"
              className="w-full object-cover max-h-52"
              onError={e => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-10 rounded-lg bg-red-600 flex items-center justify-center shadow-lg opacity-90">
                <div className="border-t-8 border-b-8 border-l-[14px] border-transparent border-l-white ml-1" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-surface-200">
            <p className="text-xs text-surface-500 font-mono truncate">{parsed.videoId}</p>
            <a
              href={`https://www.youtube.com/watch?v=${parsed.videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 shrink-0 ml-3"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </a>
          </div>
        </div>
      )}

      {inputVal && !parsed.valid && (
        <p className="text-xs text-amber-600">
          Not a recognised YouTube URL. Paste a youtube.com or youtu.be link.
        </p>
      )}
    </div>
  );
}
